import { cn } from "@prescreen/lib/utils"
import type { BankingPermissionTier } from "@prescreen/lib/types"

const ALL_TIERS: { tier: BankingPermissionTier; label: string }[] = [
  { tier: "none", label: "Off" },
  { tier: "read", label: "Read" },
  { tier: "write", label: "Write" },
]

const OFF_READ_TIERS: { tier: BankingPermissionTier; label: string }[] = [
  { tier: "none", label: "Off" },
  { tier: "read", label: "Read" },
]

export type PermissionTierTrackProps = {
  value: BankingPermissionTier
  onChange: (tier: BankingPermissionTier) => void
  labelledBy?: string
  className?: string
  /**
   * When false, only Off and Read are shown. Use for areas like RDC requests where OAuth has no
   * separate write scope—stored grants round-trip as read, so a Write control looked like updates
   * did not persist.
   */
  allowWriteTier?: boolean
}

function displayTierForTrack(
  value: BankingPermissionTier,
  allowWriteTier: boolean
): BankingPermissionTier {
  if (!allowWriteTier && value === "write") {
    return "read"
  }
  return value
}

export function PermissionTierTrack({
  value,
  onChange,
  labelledBy,
  className,
  allowWriteTier = true,
}: PermissionTierTrackProps) {
  const tiers = allowWriteTier ? ALL_TIERS : OFF_READ_TIERS
  const displayValue = displayTierForTrack(value, allowWriteTier)

  return (
    <div
      className={cn(
        "inline-flex shrink-0 rounded-full border border-border bg-muted/50 p-0.5 shadow-xs",
        className,
      )}
      role="group"
      aria-labelledby={labelledBy}
    >
      {tiers.map(({ tier, label }) => {
        const selected = displayValue === tier
        return (
          <button
            key={tier}
            type="button"
            aria-pressed={selected}
            aria-label={
              tier === "none"
                ? "Not permitted"
                : tier === "read"
                  ? "Read access"
                  : "Write access (includes read)"
            }
            className={cn(
              "rounded-full px-3 py-1.5 text-xs font-medium transition-colors outline-none",
              "focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background",
              selected
                ? "bg-background text-foreground shadow-sm"
                : "text-muted-foreground hover:text-foreground",
            )}
            onClick={() => onChange(tier)}
          >
            {label}
          </button>
        )
      })}
    </div>
  )
}
